import { AccidentAtlasApiResponse } from '../types/api'
import { AccidentAtlasRegionId, resolveRegionId } from '../types/region'
import { AccidentAtlasCmsTile, AccidentAtlasTileView } from '../types/tile'
import { AccidentAtlasView } from '../types/view'
import { deriveTileCharts } from './deriveTileCharts'
import {
  replaceInRichText,
  replaceInString,
  replacementsToMap,
} from '@tools/replacePlaceholders'

type BuildAccidentAtlasViewParams = {
  cmsTiles?: AccidentAtlasCmsTile[]
  rawData: AccidentAtlasApiResponse | null
  selection?: AccidentAtlasRegionId
}

function orderTextTilesFirst(
  tiles: AccidentAtlasTileView[]
): AccidentAtlasTileView[] {
  const textTiles: AccidentAtlasTileView[] = []
  const otherTiles: AccidentAtlasTileView[] = []

  for (const tile of tiles) {
    if (tile.displayKind === 'text') {
      textTiles.push(tile)
    } else {
      otherTiles.push(tile)
    }
  }

  return [...textTiles, ...otherTiles]
}

function buildTileView(
  cmsTile: AccidentAtlasCmsTile,
  apiTile: AccidentAtlasApiResponse[number]['tiles'][number]
): AccidentAtlasTileView {
  const placeholders = replacementsToMap(apiTile.textConfig?.replacements)
  const { displayKind, charts } = deriveTileCharts(apiTile)
  const frontHighlightText = replaceInString(
    cmsTile.frontHighlightText,
    placeholders
  )

  return {
    ...cmsTile,
    frontHeadline: replaceInString(cmsTile.frontHeadline, placeholders),
    frontCopy: replaceInRichText(cmsTile.frontCopy, placeholders),
    backHeadline: replaceInString(cmsTile.backHeadline, placeholders),
    backCopy: replaceInRichText(cmsTile.backCopy, placeholders),
    backCta:
      typeof cmsTile.backCta === 'string'
        ? replaceInString(cmsTile.backCta, placeholders)
        : replaceInRichText(cmsTile.backCta, placeholders),
    displayKind,
    charts,
    ...(frontHighlightText ? { frontHighlightText } : {}),
  }
}

export function buildAccidentAtlasView({
  cmsTiles = [],
  rawData,
  selection,
}: BuildAccidentAtlasViewParams): AccidentAtlasView | null {
  if (!rawData?.length) {
    return null
  }

  const regionId = resolveRegionId(selection)
  const region = rawData.find((entry) => entry.region_id === regionId)

  if (!region) {
    return null
  }

  const apiTilesById = new Map(region.tiles.map((tile) => [tile.tile_id, tile]))

  return {
    regionId: region.region_id,
    regionName: region.region_name,
    tiles: orderTextTilesFirst(
      cmsTiles
        .filter((cmsTile) => apiTilesById.has(cmsTile.tileId))
        .map((cmsTile) =>
          buildTileView(cmsTile, apiTilesById.get(cmsTile.tileId)!)
        )
    ),
  }
}
